<template>
  <div
    class="section"
    style="padding-top: 0px"
  >

    <div class="level">
      <a
        class="button is-primary level-left"
        @click="updatePreferences()"
      >Save Changes</a>
      <a
        class="button is-warning level-right"
        @click="resetPreferences()"
      >Reset All To Defaults</a>
    </div>

    <div class="columns">

      <div class="column">
        <div class="is-size-6 has-text-weight-semibold">
          Evaluation Inputs
        </div>

        <b-field
          class="field"
          label="MLS Market"
          custom-class="is-small"
        >
          <b-select
            expanded
            v-model="$store.getters.session.user.preferences.mlsMarket"
          >
            <option value="">Not set</option>
            <option
              v-for="(mlsMarket, key, index) in $store.state.mlsMarkets"
              :key="index"
              :value="mlsMarket.acronym"
            >{{ mlsMarket.acronym }}</option>
          </b-select>
        </b-field>
        <b-message class="is-small is-info">
          Select the MLS Market where you plan to do most of your investing. If your MLS is not listed here then select "Not Set".
        </b-message>

        <b-field
          class="field"
          label="State"
          custom-class="is-small"
        >
          <b-select
            expanded
            v-model="$store.getters.session.user.preferences.state"
          >
            <option value="">Not set</option>
            <option
              v-for="state in $store.state.states"
              :key="state"
              :value="state"
            >{{ state }}</option>
          </b-select>
        </b-field>
        <b-message class="is-small is-info">
          Select the State where you plan to do most of your investing. If you don't have a primary State, select "Not Set".
        </b-message>

        <b-field
          class="field"
          label="Sale Comps Sqft +/-"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationSalePlusMinusSqft"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 250. This value determines how close in square feet sales comps will be to your subject property.
        </b-message>

        <b-field
          class="field"
          label="Sale Comps Year Built +/-"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationSalePlusMinusYearBuilt"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 5. This value determines within how many years sales comp houses were built compared to your subject property.
        </b-message>

        <b-field
          class="field"
          label="Sale Comps Months Closed"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationSaleMonthsClosed"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 6. This value determines within how many months since today sales comp properties were sold.
        </b-message>

        <b-field
          class="field"
          label="Rent Comps Sqft +/-"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationRentPlusMinusSqft"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 250. This value determines how close in square feet rental comps will be to your subject property.
        </b-message>

        <b-field
          class="field"
          label="Rent Comps Year Built +/-"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationRentPlusMinusYearBuilt"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 5. This value determines within how many years rental comp houses were built compared to your subject property.
        </b-message>

        <b-field
          class="field"
          label="Rent Comps Months Closed"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationRentMonthsClosed"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 6. This value determines within how many months since today rental comp properties were rented out.
        </b-message>

        <b-field
          class="field"
          label="Radius in miles"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.evaluationRadius"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 1. When searching for comparables using a radius, this value determines how many miles to include in the radius. You can also use decimals, like 1.5 for one and a half miles.
        </b-message>
      </div>

      <div class="column">
        <div class="is-size-6 has-text-weight-semibold">
          Deal Inputs
        </div>

        <b-field
          class="field"
          label="Survey $"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealSurvey"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: average market rate. How much it costs to purchase a survey. You could start with $400 and change it when you have a more accurate number.
        </b-message>

        <b-field
          class="field"
          label="Appraisal $"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealAppraisal"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: average market rate. How much it costs to purchase an appraisal. You could start with $400 and change it when you have a more accurate number.
        </b-message>

        <b-field
          class="field"
          label="Inspection $"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealInspection"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: average market rate. How much it costs to perform an inspection on the property with a licensed inspector. You could start with $400 and change it when you have a more accurate number.
        </b-message>

        <b-field
          class="field"
          label="Property Insurance (% of Estimated Market Value)"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealPropertyInsurancePercentListPrice"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 1.2. This is a rough estimate of how much insurance will cost. It's calculated by multiplying the purchase price by this percentage. Different states have different rates.
        </b-message>

        <b-field
          class="field"
          label="Property Tax (% of Estimated Market Value)"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealPropertyTaxPercentListPrice"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 2.5. This is a rough estimate of how much property tax will cost. It's calculated by multiplying the purchase price by this percentage. The recommended value is appropriate for Texas. If you invest elsewhere you'll want to set a rate appropriate to your location.
        </b-message>

        <b-field
          class="field"
          label="Repairs $"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealRepairs"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 0. This is just to get you started but each property will have different estimates. If you enter 0 then plan on always entering an amount on each evaluation.
        </b-message>

        <b-field
          class="field"
          label="Max Refi Cashback $"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.dealMaxRefiCashback"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 2,000. When you refinance a hard-money loan you may get some cash back. The Max Refi Cashback is the maximum amount of cash you can receive back. Most lenders have a $2,000 cap.
        </b-message>
      </div>

      <div class="column">
        <div class="is-size-6 has-text-weight-semibold">
          Conventional Inputs
        </div>

        <b-field
          class="field"
          label="Conventional Down Payment %"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.conventionalDownPayment"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 20. This determines the default down payment % of the purchase price will be used in the financial calculations.
        </b-message>

        <b-field
          class="field"
          label="Conventional Interest Rate %"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.conventionalInterestRate"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: average market rate. This determines the default interest rate that will be used in the financial calculations.
        </b-message>

        <b-field
          class="field"
          label="Lender Fees (% of Purchase Price)"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.conventionalLenderFeesPercentOfListPrice"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 4. The cost of conventional financing will be determined by this value. It is the list price times this percentage.
        </b-message>

        <b-field
          class="field"
          label="Months Escrow"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.conventionalMonthsTaxEscrow"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 0. Specifies how much principal and interest will be added to the closing costs if you escrow. If you don't escrow set it to 0.
        </b-message>

        <b-field
          class="field"
          label="Loan Term in Years"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.conventionalLoanTermInYears"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 30. Most conventional loans are 30 year loans.
        </b-message>
      </div>

      <div class="column">
        <div class="is-size-6 has-text-weight-semibold">
          Hard Money Inputs
        </div>

        <b-field
          class="field"
          label="Loan to Value %"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardLoanToValuePercent"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 70. Hard-money lenders only lend up a percent of the after repaired value of the property.
        </b-message>

        <b-field
          class="field"
          label="Lender Fees (% of Purchase Price)"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardLenderFeesPercentOfListPrice"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">

          Suggested value: 8. Hard-money financing is usually more expensive than conventional. You pay more points. This % is just to get a ballpark number, check with your hard-money lender for exact fees.
        </b-message>

        <b-field
          class="field"
          label="Interest Rate %"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardInterestRate"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 14. Hard-money loans typically have higher interest rates than conventional. Check with your lender for exact rates.
        </b-message>

        <b-field
          class="field"
          label="Months Until Refi"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardMonthsUntilRefi"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 3. This determines how many months will go buy from the time the hard-money loan funds to when the refi loan will pay off the hard-money loan.
        </b-message>

        <b-field
          class="field"
          label="Roll In Lender Fees?"
          custom-class="is-small"
        >
          <div class="level">
            <b-switch v-model="$store.getters.session.user.preferences.hardRollInLenderFees">
              {{ displayRollIn() }}
            </b-switch>
          </div>
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: Yes. If your hard-money lender lets you pay their fees in the loan, then set this to Yes. Otherwise, leave it set to No.
        </b-message>

        <b-field
          class="field"
          label="Weeks Until Leased"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardWeeksUntilLeased"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 8. This specifies how many weeks will lapse until you have a tenant.
        </b-message>

        <b-field
          class="field"
          label="Refi Loan To Value %"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardRefiLoanToValue"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 75. Refinance loans usually only lend up to a percentage of the after repaired estimated sale price of the property.
        </b-message>

        <b-field
          class="field"
          label="Refi Loan Term in Years"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardRefiLoanTermInYears"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 30. Most refinance loans have 30 year terms.
        </b-message>

        <b-field
          class="field"
          label="Refi Interest Rate %"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardRefiInterestRate"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 5. This determines the interest rate of the refi loan.
        </b-message>

        <b-field
          class="field"
          label="Refi Lender Fees (% of Purchase Price)"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardRefiLenderFeesPercentListPrice"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 4. The cost of financing will be determined by this value. It is the list price times this percentage.
        </b-message>

        <b-field
          class="field"
          label="Refi Months Escrow"
          custom-class="is-small"
        >
          <input
            @focus="$event.target.select()"
            class="input"
            v-model="$store.getters.session.user.preferences.hardRefiMonthsTaxEscrow"
            type="number"
          >
        </b-field>
        <b-message class="is-small is-info">
          Suggested value: 2. Specifies how much principal and interest will be added to the closing costs if you escrow. If you don't escrow leave as 0.
        </b-message>
      </div>

    </div>

    <div
      class="is-size-7"
      v-if="$store.getters.session.rights.preferred"
    >

      <div class="is-divider"></div>
      <p class="subtitle">Custom Categories for MFQA 2</p>

      <b-message class="is-small is-info">
        These settings are for the MFQA 2 expense categories. The categories you enter here will be added to each MFQA 2 evaluation, saving you time.
      </b-message>

      <div class="columns">

        <div class="card column">
          <p class="has-text-weight-bold">Operating Expenses</p>
          <CustomCategories :categories="$store.getters.session.user.preferences.operatingExpenses" />
        </div>

        <div class="card column">
          <p class="has-text-weight-bold">Capital Improvements</p>
          <CustomCategories :categories="$store.getters.session.user.preferences.capitalImprovements" />
        </div>

        <div class="card column">
          <p class="has-text-weight-bold">Closing Costs</p>
          <CustomCategories :categories="$store.getters.session.user.preferences.closingCosts" />
        </div>

        <div class="card column">
          <p class="has-text-weight-bold">Start-up Costs</p>
          <CustomCategories :categories="$store.getters.session.user.preferences.startupCosts" />
        </div>

        <div class="card column">
          <p class="has-text-weight-bold">Prepaids & Deposits</p>
          <CustomCategories :categories="$store.getters.session.user.preferences.prepaidsAndDeposits" />
        </div>

      </div>

      <a
        class="button is-primary"
        @click="updatePreferences()"
      >Save Changes</a>
    </div>

  </div>
</template>

<script>
import Divider from "bulma-divider/dist/css/bulma-divider.min.css";
import CustomCategories from "@/components/settings/CustomCategories.vue";

export default {
  components: {
    Divider,
    CustomCategories
  },

  mounted() {
    this.$store.dispatch("getMlsMarkets");
    this.$store.dispatch("getStates");
  },

  data() {
    return {
      operatingExpenseInput: ""
    };
  },

  methods: {
    updatePreferences() {
      this.$store.dispatch(
        "updatePreferences",
        this.$store.getters.session.user
      );
      this.showSuccess("Preferences saved");
    },

    displayRollIn() {
      if (this.$store.getters.session.user.preferences.hardRollInLenderFees)
        return "Yes";
      else return "No";
    },

    async resetPreferences() {
      await this.$buefy.dialog.confirm({
        title: "Resetting to default values",
        message: `Reset all your preferences to default values? <b>This will replace all of your current values!</b>`,
        confirmText: "Reset All To Defaults",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          await this.$store.dispatch(
            "resetPreferences",
            this.$store.getters.session.user.id
          );
          this.$buefy.toast.open({
            message: "Preferences have been set to default values",
            type: "is-success",
            duration: 3000
          });
        }
      });
    }
  }
};
</script>

<style>
</style>
